import { motion, AnimatePresence } from "framer-motion";
import { Fuel, MessageSquare, AlertTriangle, Satellite, RadioTower, MousePointerClick, Sparkles } from "lucide-react";
import { timeAgo } from "./data";
import { goUrl } from "@/api";

const ICONS = {
  bid: { Icon: Fuel, cls: "text-gmh-orange border-gmh-orange/30 bg-gmh-orange/10" },
  click: { Icon: MousePointerClick, cls: "text-gmh-cyan border-gmh-cyan/30 bg-gmh-cyan/10" },
  msg: { Icon: MessageSquare, cls: "text-gmh-cyan border-gmh-cyan/30 bg-gmh-cyan/10" },
  alert: { Icon: AlertTriangle, cls: "text-amber-400 border-amber-500/30 bg-amber-500/10" },
  info: { Icon: Satellite, cls: "text-slate-400 border-slate-600/40 bg-slate-500/10" },
};

// Fuel + traffic names link to the listing (same path as podium/list rows:
// goUrl(listing_id), new tab, noopener — clicks count server-side).
// No id → plain text. Ship/system lines never use this.
const ListingLink = ({ listingId, onOpen, children }) => {
  if (listingId == null) return <>{children}</>;
  const href = goUrl(listingId);
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => {
        e.preventDefault();
        if (onOpen) onOpen({ id: listingId });
        else window.open(href, "_blank", "noopener,noreferrer");
      }}
      className="cursor-pointer hover:underline"
    >
      {children}
    </a>
  );
};

const Line = ({ item, onOpen }) => {
  const { Icon, cls } = ICONS[item.type] || ICONS.info;
  return (
    <div className="flex gap-3 px-4 py-3">
      <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border ${cls}`}>
        <Icon size={12} />
      </span>
      <div className="min-w-0">
        <p className="text-[13px] leading-snug text-slate-300">
          {item.type === "bid" && (
            <>
              <ListingLink listingId={item.listing_id} onOpen={onOpen}>
                <span className="font-semibold text-gmh-cyan">{item.user}</span>
              </ListingLink>{" "}
              injected <span className="mono text-gmh-orange">${item.amount}</span> of fuel.
            </>
          )}
          {item.type === "click" && (
            <>
              <ListingLink listingId={item.listing_id} onOpen={onOpen}>
                <span className="font-semibold text-gmh-cyan">{item.domain}</span>
              </ListingLink>{" "}
              received a visitor from Earth.
            </>
          )}
          {item.type === "msg" && item.user && (
            <>
              <span className="font-semibold text-gmh-cyan">{item.user}</span>:{" "}
              <span className="text-slate-300">"{item.text}"</span>
            </>
          )}
          {item.type === "msg" && !item.user && <span className="text-slate-400">{item.text}</span>}
          {(item.type === "alert" || item.type === "info") && <span className="text-slate-400">{item.text}</span>}
        </p>
        <div className="mono mt-1 text-[10px] text-slate-600">{timeAgo(item.ts)}</div>
      </div>
    </div>
  );
};

const Zone = ({ testid, label, items, emptyLabel, onOpen }) => (
  <div data-testid={testid} className="flex min-h-0 flex-1 flex-col">
    <div className="flex items-center gap-2 border-b border-[#1c2740] px-4 py-2">
      <span className="h-1.5 w-1.5 rounded-full bg-[#ff5f5f] blink-fast" />
      <span className="mono text-[10px] tracking-[0.2em] text-slate-400">{label}</span>
    </div>
    <div className="min-h-0 flex-1 overflow-y-auto divide-y divide-[#141d34]/70">
      {items.length === 0 ? (
        <div className="flex h-full items-center justify-center px-4 py-6">
          <p className="mono text-[10px] tracking-[0.15em] text-slate-600">{emptyLabel}</p>
        </div>
      ) : (
        <AnimatePresence initial={false}>
          {items.map((item) => (
            <motion.div
              key={item.id}
              layout
              initial={{ opacity: 0, x: 20, height: 0 }}
              animate={{ opacity: 1, x: 0, height: "auto" }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Line item={item} onOpen={onOpen} />
            </motion.div>
          ))}
        </AnimatePresence>
      )}
    </div>
  </div>
);

export const LiveFeed = ({ fuel = [], traffic = [], ship = [], onOpenListing }) => (
  <aside data-testid="live-mission-feed" className="panel listing-card feed-panel flex h-full max-h-[720px] flex-col">
    <div className="flex items-center justify-between border-b border-[#1c2740] px-4 py-3">
      <div className="mono flex items-center gap-2 text-[11px] tracking-[0.25em] text-slate-300">
        <RadioTower size={14} className="text-gmh-cyan" /> LIVE MISSION FEED
      </div>
    </div>
    <div className="flex min-h-0 flex-1 flex-col divide-y divide-[#141d34]/50">
      <div className="flex min-h-0 basis-[40%] flex-col overflow-hidden">
        <Zone
          testid="feed-zone-fuel"
          label="FUEL INJECTIONS"
          items={fuel}
          emptyLabel="Awaiting first fuel from Earth…"
          onOpen={onOpenListing}
        />
      </div>
      <div className="flex min-h-0 basis-[40%] flex-col overflow-hidden">
        <Zone
          testid="feed-zone-traffic"
          label="EARTH TRAFFIC"
          items={traffic}
          emptyLabel="No Earth traffic yet…"
          onOpen={onOpenListing}
        />
      </div>
      <div className="flex min-h-0 basis-[20%] flex-col overflow-hidden">
        <Zone
          testid="feed-zone-ship"
          label="SHIP COMMS"
          items={ship}
          emptyLabel="Listening for ship telemetry…"
          onOpen={onOpenListing}
        />
      </div>
    </div>
    <div className="mono flex items-center gap-2 border-t border-[#1c2740] px-4 py-2.5 text-[10px] tracking-[0.25em] text-slate-500">
      <Sparkles size={12} className="text-gmh-cyan" /> THREE-CHANNEL UPLINK ACTIVE
    </div>
  </aside>
);
